import { Temporal } from '@js-temporal/polyfill';
import { CareerFocusWithMetrics } from '../hooks/useCareerFocus';

export enum InternshipStatus {
  UPCOMING = 'upcoming',
  ACTIVE = 'active',
  COMPLETED = 'completed'
}

export interface InternshipStatusResult {
  status: InternshipStatus;
  metadata: {
    daysUntilStart?: number;
    daysSinceEnd?: number;
    currentWeek?: number;
    isOpenEnded?: boolean;
  };
}

export function getInternshipStatus(focus: CareerFocusWithMetrics): InternshipStatusResult {
  const today = Temporal.Now.plainDateISO();
  const startDate = Temporal.PlainDate.from(focus.start_date);
  const endDate = focus.end_date ? Temporal.PlainDate.from(focus.end_date) : null;
  
  // Check if internship hasn't started yet
  if (Temporal.PlainDate.compare(startDate, today) > 0) {
    const daysUntilStart = startDate.since(today, { largestUnit: 'days' }).days;
    return {
      status: InternshipStatus.UPCOMING,
      metadata: { daysUntilStart }
    };
  }
  
  // Check if internship has ended
  if (endDate && Temporal.PlainDate.compare(endDate, today) < 0) {
    const daysSinceEnd = today.since(endDate, { largestUnit: 'days' }).days;
    return {
      status: InternshipStatus.COMPLETED,
      metadata: { daysSinceEnd }
    };
  }
  
  // Internship is active
  const weeksElapsed = Math.floor(
    today.since(startDate, { largestUnit: 'days' }).days / 7
  ) + 1;
  
  return {
    status: InternshipStatus.ACTIVE,
    metadata: { 
      currentWeek: weeksElapsed,
      isOpenEnded: !endDate
    }
  };
}

// Enhanced duration calculation for open-ended internships
export function getAdaptiveInternshipDuration(
  focus: CareerFocusWithMetrics,
  durationConfig?: {
    expected_duration_weeks: number | null;
    auto_extend: boolean;
    extension_buffer_weeks: number;
  }
): number {
  if (!focus.end_date) {
    // If we have an expected duration configured, use it
    if (durationConfig?.expected_duration_weeks) {
      return durationConfig.expected_duration_weeks;
    }
    
    const status = getInternshipStatus(focus);
    const weeksElapsed = status.metadata.currentWeek || 0;
    
    // If auto-extend is disabled, return elapsed time + buffer
    if (durationConfig && !durationConfig.auto_extend) {
      return weeksElapsed + (durationConfig.extension_buffer_weeks || 4);
    }
    
    // Default adaptive duration buckets for open-ended internships
    if (weeksElapsed < 12) return 12;      // Assume standard 3-month
    if (weeksElapsed < 24) return 24;      // Assume 6-month
    if (weeksElapsed < 52) return 52;      // Assume 1-year
    return weeksElapsed + (durationConfig?.extension_buffer_weeks || 4);  // Rolling lookahead
  }
  
  // For internships with end dates, calculate actual duration
  const start = Temporal.PlainDate.from(focus.start_date);
  const end = Temporal.PlainDate.from(focus.end_date);
  const days = end.since(start, { largestUnit: 'days' }).days;
  return Math.ceil(days / 7);
}

// Calculate phases for extended internships
export interface PhaseBoundary {
  phase: string;
  weeks: number;
  startWeek: number;
  endWeek: number;
}

export function calculateExtendedPhases(
  totalWeeks: number,
  phaseConfig?: {
    phases: Array<{
      name: string;
      percentage: number;
      min_weeks: number;
    }>;
  }
): PhaseBoundary[] {
  let phases: PhaseBoundary[] = [];
  let currentWeek = 1;
  
  // Use provided config or defaults
  const config = phaseConfig?.phases || [
    { name: 'onboarding', percentage: 0.08, min_weeks: 1 },
    { name: 'foundation', percentage: 0.17, min_weeks: 2 },
    { name: 'growth', percentage: 0.25, min_weeks: 3 },
    { name: 'impact', percentage: 0.35, min_weeks: 4 },
    { name: 'transition', percentage: 0.15, min_weeks: 2 }
  ];
  
  // For 6-12 month internships, use percentage-based scaling
  if (totalWeeks <= 52) {
    config.forEach(phaseItem => {
      const weeks = Math.max(
        phaseItem.min_weeks || 1, 
        Math.ceil(totalWeeks * phaseItem.percentage)
      );
      phases.push({
        phase: phaseItem.name,
        weeks,
        startWeek: currentWeek,
        endWeek: currentWeek + weeks - 1
      });
      currentWeek += weeks;
    });
  } else {
    // For > 1 year internships, add advanced phases
    const fixedPhases = [
      { phase: 'onboarding', weeks: 4 },
      { phase: 'foundation', weeks: 8 },
      { phase: 'growth', weeks: 16 },
      { phase: 'impact', weeks: 20 }
    ];
    
    fixedPhases.forEach(config => {
      phases.push({
        phase: config.phase,
        weeks: config.weeks,
        startWeek: currentWeek,
        endWeek: currentWeek + config.weeks - 1
      });
      currentWeek += config.weeks;
    });
    
    // Add leadership phase for remaining time
    const remainingWeeks = totalWeeks - 52;
    if (remainingWeeks > 0) {
      phases.push({
        phase: 'leadership',
        weeks: remainingWeeks,
        startWeek: currentWeek,
        endWeek: currentWeek + remainingWeeks - 1
      });
      currentWeek += remainingWeeks;
    }
    
    // Add transition phase at the end
    phases.push({
      phase: 'transition',
      weeks: 4,
      startWeek: currentWeek,
      endWeek: currentWeek + 3
    });
  }
  
  return phases;
}

// Get yearly milestones for multi-year internships
export interface YearlyMilestone {
  year: number;
  weekStart: number;
  weekEnd: number;
  label: string;
  suggestedReview: string;
}

export function getYearlyMilestones(focus: CareerFocusWithMetrics): YearlyMilestone[] {
  const totalWeeks = getAdaptiveInternshipDuration(focus);
  const milestones: YearlyMilestone[] = [];
  
  for (let year = 1; year <= Math.ceil(totalWeeks / 52); year++) {
    milestones.push({
      year,
      weekStart: (year - 1) * 52 + 1,
      weekEnd: Math.min(year * 52, totalWeeks),
      label: `Year ${year}`,
      suggestedReview: `Annual Review - Year ${year}`
    });
  }
  
  return milestones;
}

// Format status display text
export function formatInternshipStatusText(status: InternshipStatusResult): string {
  switch (status.status) {
    case InternshipStatus.UPCOMING:
      const days = status.metadata.daysUntilStart || 0;
      if (days === 0) return 'Starting today';
      if (days === 1) return 'Starting tomorrow';
      if (days <= 7) return `Starting in ${days} days`;
      if (days <= 30) return `Starting in ${Math.ceil(days / 7)} weeks`;
      return `Starting in ${Math.ceil(days / 30)} months`;
      
    case InternshipStatus.COMPLETED:
      const daysSince = status.metadata.daysSinceEnd || 0;
      if (daysSince === 0) return 'Completed today';
      if (daysSince === 1) return 'Completed yesterday';
      if (daysSince <= 7) return `Completed ${daysSince} days ago`;
      if (daysSince <= 30) return `Completed ${Math.ceil(daysSince / 7)} weeks ago`;
      return `Completed ${Math.ceil(daysSince / 30)} months ago`;
      
    case InternshipStatus.ACTIVE:
      const week = status.metadata.currentWeek || 1;
      if (status.metadata.isOpenEnded) {
        return `Week ${week} • Ongoing`;
      }
      return `Week ${week}`;
  }
}